import { useGlobalState, useStatsigLayer } from '@/hooks';
import type { IPmtOnboardingPopup } from '@/modules/pmt/interfaces';
import { getTextByLanguage } from '@/modules/pmt/utils';

export const usePmtOnboardingPopupCopy = (popUp: IPmtOnboardingPopup | undefined): IPmtOnboardingPopup | undefined => {
  const { isLoggedIn } = useGlobalState();
  const {
    get,
    getEnglishOnlyText,
    isReady: isAnonymousReady,
    isEnglishPage,
  } = useStatsigLayer('track_my_parcel_copy_layer');
  const { get: getSignedIn, isReady: isSignedInReady } = useStatsigLayer('track_my_parcel_signed_in_layer');

  if (!popUp) return undefined;

  // Non-English pages keep the CMS popup as-is: proposition items hold per-language records the popup resolves itself,
  // and the Statsig hero image is only tested on English pages
  if (!isEnglishPage || !isAnonymousReady || !isSignedInReady) return popUp;

  const mergedPropositionList = popUp.joinUsValuePropositionList?.map((item, index) => {
    const i = index + 1;
    return {
      ...item,
      title: getEnglishOnlyText(`proposition_${i}_title`, getTextByLanguage(item.title ?? '', 'en') ?? ''),
      description: getEnglishOnlyText(
        `proposition_${i}_description`,
        getTextByLanguage(item.description ?? '', 'en') ?? '',
      ),
    };
  });

  // Hero image is tested per audience: guests bucket by stableID, signed-in users by userID.
  // Empty by default so the CMS hero image stays in place when the experiment does not set it
  const heroImageUrl = (isLoggedIn ? getSignedIn : get)('hero_image_url', '');

  // Drop the CMS contentType: it describes the CMS asset (e.g. SVG), not the Statsig one
  const heroImage = heroImageUrl ? { ...popUp.heroImage, url: heroImageUrl, contentType: undefined } : popUp.heroImage;

  return {
    ...popUp,
    heroImage,
    joinUsHeader: getEnglishOnlyText('join_us_header', popUp.joinUsHeader ?? ''),
    joinUsValuePropositionList: mergedPropositionList ?? popUp.joinUsValuePropositionList,
    joinUsDesktopMainText: getEnglishOnlyText('join_us_main_text', popUp.joinUsDesktopMainText ?? ''),
    joinUsSubtext: getEnglishOnlyText('join_us_subtext', popUp.joinUsSubtext ?? ''),
    subPmcNewsletterText: getEnglishOnlyText('newsletter_checkbox_label', popUp.subPmcNewsletterText ?? ''),
    newsletterSubText: getEnglishOnlyText('newsletter_subtext', popUp.newsletterSubText ?? ''),
  };
};
